import { Controller } from "@hotwired/stimulus"

// ปุ่ม "แชร์ดวงวันนี้" — วาดการ์ดสรุปดวงเป็นรูป PNG ด้วย canvas
// แล้วเปิดเมนูแชร์ของเครื่อง (LINE/IG/บันทึกรูป) พร้อมข้อความ + ลิงก์เว็บ
// ถ้าเครื่องไม่รองรับ share ไฟล์ ก็ดาวน์โหลดรูปแทน (เซฟรูปได้เสมอ)
export default class ShareDailyController extends Controller {
  static values = { payload: Object }

  W = 1080
  MARGIN = 80
  FONT = "'Prompt', 'Sarabun', 'Noto Sans Thai', sans-serif"

  async share() {
    const d = this.payloadValue
    const blob = await this.renderCard(d)
    const file = new File([blob], "lagnara-daily.png", { type: "image/png" })
    const url = window.location.origin
    const text = `ดวง${d.date} · ลัคนา${d.lakhana}\n${this.starsText(d.score)} ${d.level || ""}\nเช็กดวงรายวันของคุณได้ที่ lagnara`

    // แชร์พร้อมไฟล์ + ข้อความ + ลิงก์ (LINE/IG/บันทึกรูป อยู่ในเมนูเดียว)
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text, url, title: "ดวงวันนี้ · lagnara" })
        return
      } catch (e) {
        if (e.name === "AbortError") return
      }
    }

    // fallback: ดาวน์โหลดรูป (เดสก์ท็อป/เบราว์เซอร์ที่ไม่รองรับ share ไฟล์)
    this.saveBlob(blob)
  }

  // ปุ่ม "บันทึกรูป" — ดาวน์โหลดรูปลงเครื่องตรง ๆ (ใช้ได้ทุกแพลตฟอร์ม
  // โดยเฉพาะเดสก์ท็อปที่เมนูแชร์ไม่มีตัวเลือกเซฟรูป)
  async download() {
    const blob = await this.renderCard(this.payloadValue)
    this.saveBlob(blob)
  }

  saveBlob(blob) {
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = objectUrl
    a.download = "lagnara-daily.png"
    a.click()
    URL.revokeObjectURL(objectUrl)
  }

  starsText(score) {
    const n = Math.max(0, Math.min(5, score || 0))
    return "★".repeat(n) + "☆".repeat(5 - n)
  }

  async renderCard(d) {
    // โหลดฟอนต์ให้เสร็จก่อนวาด — ต้องโหลดครบทุกน้ำหนักที่ใช้ในการ์ด (300/400/500/600/700)
    // ไม่งั้นบางบรรทัดจะสลับไปใช้ฟอนต์สำรองตอนวาดจริง (กว้าง/แคบกว่าที่วัดไว้ตอนตัดคำ) แล้วล้นขอบภาพ
    try {
      await document.fonts.ready
      await Promise.all(
        [300, 400, 500, 600, 700].map(w => document.fonts.load(`${w} 40px ${this.FONT}`))
      )
    } catch (_) { /* ไม่มี document.fonts ก็วาดด้วยฟอนต์ระบบ */ }

    const W = this.W
    const M = this.MARGIN
    const inner = W - M * 2

    // วัดความสูงก่อน เพื่อกำหนดขนาด canvas ให้พอดี (ไม่ตัดขอบ)
    const meas = document.createElement("canvas").getContext("2d")
    const adviceLines = this.wrapLines(meas, `💡 ${d.advice || ""}`, `300 38px ${this.FONT}`, inner)
    const topics = d.topics || []

    let h = 130 + 70 + 56 + 100 + 60 // brand, date, lakhana, stars, level
    h += 56                          // moon line
    h += adviceLines.length * 50 + 30
    if (d.dos) h += 150
    if (topics.length) h += 60 + topics.length * 96
    h += 150                         // colors row
    h += 90                          // footer
    const H = Math.round(h)

    const canvas = document.createElement("canvas")
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext("2d")

    // พื้นหลังไล่เฉด + กรอบส้ม
    const bg = ctx.createLinearGradient(0, 0, 0, H)
    bg.addColorStop(0, "#1c130b")
    bg.addColorStop(1, "#0d0d0d")
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, W, H)
    ctx.strokeStyle = "#FE4F02"
    ctx.lineWidth = 6
    this.roundRect(ctx, 30, 30, W - 60, H - 60, 36)
    ctx.stroke()

    ctx.textAlign = "center"
    let y = 120

    ctx.fillStyle = "#FE4F02"
    ctx.font = `700 54px ${this.FONT}`
    ctx.fillText("lagnara", W / 2, y)
    y += 66

    ctx.fillStyle = "#f5ede2"
    ctx.font = `400 40px ${this.FONT}`
    ctx.fillText(this.fit(ctx, `ดวง${d.date}`, inner), W / 2, y)
    y += 52
    ctx.font = `400 38px ${this.FONT}`
    ctx.fillText(this.fit(ctx, `ลัคนา${d.lakhana}`, inner), W / 2, y)
    y += 74

    // ดาวคะแนนวัน
    ctx.fillStyle = "#f6d55c"
    ctx.font = "78px sans-serif"
    ctx.fillText(this.starsText(d.score), W / 2, y)
    y += 60
    ctx.fillStyle = "#FE4F02"
    ctx.font = `500 44px ${this.FONT}`
    ctx.fillText(d.level || "", W / 2, y)
    y += 56

    // เฟสจันทร์
    if (d.moon_emoji || d.lunar) {
      ctx.fillStyle = "#c8bda8"
      ctx.font = `400 36px ${this.FONT}`
      ctx.fillText(this.fit(ctx, [d.moon_emoji, d.lunar].filter(Boolean).join(" "), inner), W / 2, y)
    }
    y += 50

    // คำแนะนำ (fit() ซ้ำเป็น safety net เผื่อฟอนต์ตอนวาดจริงกว้างกว่าตอนตัดคำ)
    ctx.fillStyle = "#e8ddca"
    ctx.font = `300 38px ${this.FONT}`
    adviceLines.forEach(line => {
      ctx.fillText(this.fit(ctx, line, inner), W / 2, y)
      y += 50
    })
    y += 30

    // ควรทำ — กล่องเดียวเต็มความกว้าง (เลิกคู่เขียว/แดง เหลือ Do อย่างเดียว 12 ส.ค. 69)
    if (d.dos) {
      this.miniBox(ctx, M, y, inner, "✓ ควรทำ", d.dos, "#7fd48a")
      y += 150
    }

    // คะแนนรายหมวด (label บน, แถบเต็มความกว้าง — ไม่ทับกัน)
    if (topics.length) {
      ctx.textAlign = "left"
      ctx.fillStyle = "#f5ede2"
      ctx.font = `600 40px ${this.FONT}`
      ctx.fillText("คะแนนรายหมวด", M, y + 8)
      y += 56
      topics.forEach(t => {
        ctx.textAlign = "left"
        ctx.font = `400 36px ${this.FONT}`
        ctx.fillStyle = "#f5ede2"
        ctx.fillText(this.fit(ctx, `${t.emoji} ${t.name}`, inner - 100), M, y)
        ctx.textAlign = "right"
        ctx.font = `600 36px ${this.FONT}`
        ctx.fillStyle = "#FE4F02"
        ctx.fillText(String(t.score ?? ""), W - M, y)
        y += 20
        ctx.fillStyle = "#3a3229"
        this.roundRect(ctx, M, y, inner, 20, 10)
        ctx.fill()
        ctx.fillStyle = t.score >= 70 ? "#f6d55c" : (t.score >= 50 ? "#e8a94f" : "#e06c4f")
        this.roundRect(ctx, M, y, Math.max(inner * (t.score || 0) / 100, 20), 20, 10)
        ctx.fill()
        y += 56
      })
      y += 10
    }

    // สีมงคล / สีควรเลี่ยง / เวลาดี
    ctx.textAlign = "center"
    const cols = [
      ["สีมงคล", d.lucky_color || "—"],
      ["สีควรเลี่ยง", d.avoid_color || "—"],
      ["เวลาดี", d.good_time || "—"]
    ]
    cols.forEach(([label, value], i) => {
      const cx = M + inner / 6 + (i * inner / 3)
      ctx.fillStyle = "#b8ad9c"
      ctx.font = `300 32px ${this.FONT}`
      ctx.fillText(label, cx, y)
      ctx.fillStyle = "#f5ede2"
      ctx.font = `600 38px ${this.FONT}`
      ctx.fillText(this.fit(ctx, value, inner / 3 - 20), cx, y + 52)
    })
    y += 120

    ctx.fillStyle = "#8f8574"
    ctx.font = `300 32px ${this.FONT}`
    ctx.fillText(this.fit(ctx, `เช็กดวงของคุณได้ที่ ${window.location.host || "lagnara"}`, inner), W / 2, y)

    return new Promise(resolve => canvas.toBlob(resolve, "image/png"))
  }

  miniBox(ctx, x, y, w, title, body, color) {
    const inner = this.W - this.MARGIN * 2
    ctx.fillStyle = "rgba(255,255,255,0.05)"
    this.roundRect(ctx, x, y, w, 130, 16)
    ctx.fill()
    ctx.textAlign = "left"
    ctx.fillStyle = color
    ctx.font = `600 32px ${this.FONT}`
    ctx.fillText(this.fit(ctx, title, w - 48), x + 24, y + 44)
    ctx.fillStyle = "#e8ddca"
    ctx.font = `300 30px ${this.FONT}`
    const lines = this.wrapLines(ctx, body || "—", `300 30px ${this.FONT}`, w - 48).slice(0, 2)
    lines.forEach((line, i) => ctx.fillText(this.fit(ctx, line, w - 48), x + 24, y + 84 + i * 36))
  }

  // ตัดคำเป็นบรรทัดตามความกว้าง (ทีละอักขระ เหมาะกับภาษาไทยที่ไม่มีเว้นวรรค)
  wrapLines(ctx, text, font, maxWidth) {
    ctx.font = font
    const lines = []
    let line = ""
    for (const ch of Array.from(text)) {
      if (ch === "\n") { lines.push(line); line = ""; continue }
      if (ctx.measureText(line + ch).width > maxWidth && line !== "") {
        lines.push(line)
        line = ch
      } else {
        line += ch
      }
    }
    if (line) lines.push(line)
    return lines
  }

  // ย่อข้อความบรรทัดเดียวให้พอดีความกว้าง (ใส่ … ถ้าเกิน)
  fit(ctx, text, maxWidth) {
    if (ctx.measureText(text).width <= maxWidth) return text
    let t = text
    while (t.length > 1 && ctx.measureText(t + "…").width > maxWidth) t = t.slice(0, -1)
    return t + "…"
  }

  roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
  }
}
